import * as am5 from '@amcharts/amcharts5';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeESCL from '@angular/common/locales/es-CL';
import localeESCLExtra from '@angular/common/locales/extra/es-CL';
import {
  APP_INITIALIZER,
  EnvironmentProviders,
  ErrorHandler,
  LOCALE_ID,
  Provider,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  ActivatedRouteSnapshot,
  Router,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { BpHistoryService } from 'bp/shared/services/bp-history.service';
import { SelectivePreloadStrategy } from 'bp/shared/services/selective-preload.strategy';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '@environment/environment';
import { provideFuse } from '@fuse';
import { ImprobableEngGrpcWebClientModule } from '@ngx-grpc/improbable-eng-grpc-web-client';

import Hotjar from '@hotjar/browser';
import { GrpcMessage } from '@ngx-grpc/common';
import { GRPC_INTERCEPTORS, GrpcCoreModule } from '@ngx-grpc/core';
import * as Sentry from '@sentry/angular';
import {
  replayCanvasIntegration,
  replayIntegration,
  httpClientIntegration,
} from '@sentry/browser';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { IPasswordStrengthMeterService } from 'angular-password-strength-meter';
import { Angulartics2Module } from 'angulartics2';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideTransloco } from 'app/core/transloco/transloco.provider';
import { bpAppRoutes, notMatchRedirectRoutes } from 'bp/app.routing';
import { BpPendingNotificationsService } from 'bp/shared/services/bp-pending-notifications.service';
import { ComponentFactoryService } from 'bp/shared/services/component-factory.service';
import { UserStateService } from 'bp/shared/services/user-state.service';
import 'moment/locale/es';

// import { H } from 'highlight.run';

import { GrpcLoggerModule } from '@bp-core/src/lib/modules/grpc-logger/grpc-logger.module';
import { BP_CONFIRMATION_SERVICE, BpGrpcErrorInterceptor } from '@bp-core/src/lib/modules/grpc-error';
import { BP_ERROR_DIALOG_ACTIONS } from 'bp/shared/models/confirmation.types';
import { BpConfirmationService } from 'bp/shared/services/bp-confirmation.service';
import { QualifiedInvestorErrorAction } from 'bp/shared/services/qualified-investor-error-action';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { grpc } from '@improbable-eng/grpc-web';
import { provideLumberjack } from '@ngworker/lumberjack';
import { provideLumberjackConsoleDriver } from '@ngworker/lumberjack/console-driver';

import { provideClarity } from 'ngx-clarity';
import { MarkdownModule } from 'ngx-markdown';
import { OAuthModule } from 'projects/bp-core/src/lib/external/angular-oauth2-oidc-jwks/public_api';
import { AuthGrpcInterceptor } from 'projects/bp-core/src/lib/services/portal/auth-grpc.interceptor';
import { BP_INCOMPLETE_ACCOUNT_REDIRECT } from 'projects/bp-core/src/lib/services/portal/bp-incomplete-account.service';
import { GrpcLoadingInterceptor } from 'projects/bp-portal/src/bp/shared/interceptors/grpc-loading.interceptor';
import { BpLoadingInterceptor } from 'projects/bp-portal/src/bp/shared/interceptors/loading.interceptor';
import { AppInteractionService } from '../../bp-core/src/lib/services/app-interaction.service';
import { AppComponent } from './app/app.component';
import { CustomPsmServiceService } from './app/customPsmService.service';
import { DevelopErrorHandler } from './app/develop-error-handler.service';
import { bpLoopBackApi } from './app/mock-api';
import { INCOMPLETE_ACCOUNT_REDIRECT } from './bp/modules/get-started/models/incomplete-registration';
import { ProductionErrorHandler } from './bp/shared/handlers/production-error.handler';
import { BpHeaderService } from './bp/shared/services/bp-header.service';
import { ErrorFilterService } from './bp/shared/services/error-filter.service';
import { PortalConfigParamService } from './bp/shared/services/portal-config-param.service';
import { ConsoleErrorInterceptor } from './bp/shared/utils/console-error-interceptor';
import { AsesorGuardiaEscrituraInterceptor } from './bp/modules/asesor-ia/services/asesor-guardia.interceptor';
// Swiper ya NO se registra acá: el import estático de swiper/element/bundle
// metía 178 KB al bundle inicial para un carrusel que solo usan dashboard y
// discover (lazy). Cada uno registra los custom elements al cargar su chunk.

// ⚠️ ORDEN CRÍTICO DE INICIALIZACIÓN - NO MOVER ESTE BLOQUE
// Interceptar console.error/log/warn ANTES de que DataDog se inicialice
// DEBE ejecutarse ANTES de datadogRum.init() y datadogLogs.init()
// para que el filtrado funcione correctamente.
//
// NOTA: El interceptor se inicializa aquí con una instancia temporal.
// El ProductionErrorHandler actualizará esta instancia con la singleton de Angular.
// Esto es necesario porque necesitamos filtrar ANTES de que Angular se inicialice.
// Variable para almacenar el servicio de filtrado que será usado por DataDog
let errorFilterServiceInstance: ErrorFilterService | null = null;

if (environment.name === 'Production') {
  // Crear instancia temporal del servicio de filtrado
  // Esta será reemplazada por la instancia singleton cuando Angular se inicialice
  const tempErrorFilterService = new ErrorFilterService();

  // Guardar referencia para usar en DataDog beforeSend
  errorFilterServiceInstance = tempErrorFilterService;

  // Inicializar el interceptor de console.error/log/warn
  // Esto reemplazará los métodos de consola con versiones filtradas
  ConsoleErrorInterceptor.initialize(tempErrorFilterService);

  // NO usar console.log aquí porque ya está interceptado
  // Solo en desarrollo local para verificar
}

am5.addLicense(environment.am5chartLicense);
const portalRoutes = [...bpAppRoutes, ...notMatchRedirectRoutes];
const xhr = grpc.CrossBrowserHttpTransport({});
const ws = grpc.WebsocketTransport();
const providers: (EnvironmentProviders | Provider)[] = [
  provideClarity({
    enabled: environment.name === 'Production',
    projectId: 'hrh0x7c9ow',
  }),
  // Cuenta solo en Identity (BET-515): bp-portal tiene `/register`; bp-advisor y bp-admin no lo proveen.
  { provide: BP_INCOMPLETE_ACCOUNT_REDIRECT, useValue: INCOMPLETE_ACCOUNT_REDIRECT },
  // Asesor IA operando el portal: corta RPCs de escritura disparadas por una acción del asesor (el clic final
  // es del cliente). Va primero para cortar la cadena antes del interceptor global de errores.
  { provide: GRPC_INTERCEPTORS, useClass: AsesorGuardiaEscrituraInterceptor, multi: true },
  { provide: GRPC_INTERCEPTORS, useClass: GrpcLoadingInterceptor, multi: true },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: BpLoadingInterceptor,
    multi: true,
  },
  {
    provide: IPasswordStrengthMeterService,
    useClass: CustomPsmServiceService,
  },
  // CustomRouter,
  // {
  //   provide: Router,
  //   useClass: CustomRouter,
  // },
  /*{
    provide: FUSE_CONFIG,
    useValue: appConfig,
  },*/
  {
    provide: LOCALE_ID,
    useValue: 'es-CL',
  },
  {
    provide: GRPC_INTERCEPTORS,
    useClass: AuthGrpcInterceptor,
    multi: true,
  },
  // Interceptor global de errores gRPC: parsea cada GrpcStatusEvent de error,
  // muestra dialog automático si el servidor lo marcó displayable y re-propaga
  // siempre. El guard anti-doble-dialog vive en BpErrorHandlerService.showError.
  {
    provide: GRPC_INTERCEPTORS,
    useClass: BpGrpcErrorInterceptor,
    multi: true,
  },
  {
    provide: DateAdapter,
    useClass: LuxonDateAdapter,
  },
  { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  {
    provide: MAT_DATE_FORMATS,
    useValue: {
      parse: {
        dateInput: 'D',
      },
      display: {
        dateInput: 'DDD',
        monthYearLabel: 'LLL yyyy',
        dateA11yLabel: 'DD',
        monthYearA11yLabel: 'LLLL yyyy',
      },
    },
  },

  // Transloco Config
  provideTransloco(),

  // Fuse
  provideAuth(),
  provideIcons(),
  provideFuse({
    mockApi: {
      delay: 0,
      // Solo lo que el portal consume del mock (menú base + GoalService):
      // mockApiServices completo instanciaba 24 clases demo de Fuse y anclaba
      // ~1.1 MB de fixtures (mailbox, contacts, chat…) al bundle inicial.
      services: bpLoopBackApi,
    },
    fuse: {
      layout: 'classy',
      scheme: 'light',
      screens: {
        sm: '600px',
        md: '960px',
        lg: '1280px',
        xl: '1440px',
      },
      theme: 'theme-bp',
      themes: [
        {
          id: 'theme-bp',
          name: 'Betterplan',
        },
        {
          id: 'theme-default',
          name: 'Default',
        },
        {
          id: 'theme-brand',
          name: 'Brand',
        },
        {
          id: 'theme-teal',
          name: 'Teal',
        },
        {
          id: 'theme-rose',
          name: 'Rose',
        },
        {
          id: 'theme-purple',
          name: 'Purple',
        },
        {
          id: 'theme-amber',
          name: 'Amber',
        },
      ],
    },
  }),
  BpPendingNotificationsService,
  UserStateService,
  AppInteractionService,
  ComponentFactoryService,
  BpHeaderService,
  // Se instancia al arrancar para que anote el historial desde la primera navegación.
  provideAppInitializer(() => {
    inject(BpHistoryService);
  }),
  PortalConfigParamService,
  provideLumberjack(),
  provideLumberjackConsoleDriver(),
  // gRPC Error Handling - connects BpErrorHandlerService with BpConfirmationService
  { provide: BP_CONFIRMATION_SERVICE, useExisting: BpConfirmationService },
  // Acciones que las features le suman al diálogo de un error del servidor, que abre el
  // interceptor global sin pasar por ningún componente.
  { provide: BP_ERROR_DIALOG_ACTIONS, useClass: QualifiedInvestorErrorAction, multi: true },
];
/**
 * Router en forma standalone (`provideRouter` + features), equivalente moderno del
 * `RouterModule.forRoot(routes, ExtraOptions)` anterior. La migración existe por UNA
 * razón: `ExtraOptions.enableViewTransitions` es un booleano sin opciones, y acá
 * necesitamos `onViewTransitionCreated` para SALTAR el view transition cuando la
 * navegación solo cambia fragment/query params (tabs por fragment, como
 * summary ⇄ activities del detalle de acción).
 *
 * Por qué: `startViewTransition` congela la pantalla con un snapshot del estado viejo
 * mientras monta el nuevo y termina con un crossfade. En rutas distintas eso se ve bien,
 * pero en tabs de la MISMA vista tapaba las animaciones propias del header (medido en
 * trace: pantalla congelada ~400ms y salto seco, peor cuanto más ancha la ventana, y
 * `compositeFailed` en las transiciones CSS mientras el overlay estaba activo).
 */
const routerProviders = provideRouter(
  portalRoutes,
  // Preload selectivo (data.preload): PreloadAllModules bajaba ~30 módulos
  // ajenos al home tras la primera navegación, compitiendo con los datos.
  withPreloading(SelectivePreloadStrategy),
  withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
  // 'computed': el router numera cada entrada del historial. BpHistoryService lo usa para
  // sacar los flujos del camino de "atrás" (también el de Android y el gesto de iOS). Además,
  // una navegación cancelada vuelve a la entrada en que estaba, en vez de pisar su URL.
  withRouterConfig({ canceledNavigationResolution: 'computed' }),
  withViewTransitions({
    onViewTransitionCreated: ({ transition, from, to }) => {
      // Una página que sigue montada y anima su propio cambio (Mi cuenta: el «volver» del
      // título al entrar o salir de una sección) se marca con `ownTransitions` en su ruta: el
      // fundido de pantalla completa tapaba esa animación y la flecha titilaba.
      const fromRoute = deepestRoute(from).routeConfig;
      if (fromRoute?.data?.['ownTransitions'] && fromRoute === deepestRoute(to).routeConfig) {
        transition.skipTransition();
        return;
      }
      const router = inject(Router);
      const targetUrl = router.getCurrentNavigation()?.finalUrl;
      if (!targetUrl) {
        return;
      }
      // Misma ruta y mismos params: solo cambian fragment/query → sin view transition.
      const isSameView = router.isActive(targetUrl, {
        paths: 'exact',
        matrixParams: 'exact',
        fragment: 'ignored',
        queryParams: 'ignored',
      });
      if (isSameView) {
        transition.skipTransition();
      }
    },
  }),
);

if (environment.name === 'Production') {
  // H.init('zg0xprng', {
  //   environment: environment.name,
  //   version: environment.version,
  //   reportConsoleErrors: true,
  //   serviceName: 'bp-portal',
  //   privacySetting: 'none',
  //   enableCanvasRecording: true,
  //   enablePerformanceRecording: true,
  //   tracingOrigins: [
  //     window.location.origin,
  //     /^https:\/\/[^/]+\.betterplan\.cl\/.*/,
  //     /^https:\/\/[^/]+\.fintoc\.com\/.*/,
  //   ],
  //   networkRecording: {
  //     enabled: true,
  //     recordHeadersAndBody: true,
  //     urlBlocklist: [
  //       // insert full or partial urls that you don't want to record here
  //       // Out of the box, Highlight will not record these URLs (they can be safely removed):
  //       'https://www.googleapis.com/identitytoolkit',
  //       'https://securetoken.googleapis.com',
  //     ],
  //   },
  // });
  datadogRum.init({
    applicationId: '728dbaf9-e9c2-4729-9fa4-3feb8f969f65',
    clientToken: 'pub6a9aac5415bd8b20180b597ae4750b43',
    site: 'us3.datadoghq.com',
    service: 'bp-client-portal',
    env: environment.name.toLowerCase(),
    version: environment.version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // Enmascara el texto y los campos de entrada en la grabación de sesión antes de que
    // salga del navegador: la pantalla del cliente muestra RUT, saldos y patrimonio, y el
    // destino de Datadog está fuera de Chile. Se conserva el recorrido (clics, pantallas,
    // errores), que es lo que sirve para diagnosticar, y se pierde solo el contenido.
    // El patrimonio es dato sensible bajo la Ley 21.719.
    defaultPrivacyLevel: 'mask',
    // Con `mask`, el nombre de las acciones (clics) también se enmascara: el SDK arma el nombre con
    // el texto del botón o del elemento clicado, que puede traer datos del cliente, y ese nombre viaja
    // aparte de la grabación. Se conserva poniendo `data-dd-action-name` en los botones que importe
    // distinguir en los reportes.
    enablePrivacyForActionName: true,
    allowedTracingUrls: [
      window.location.origin,
      /^https:\/\/[^/]+\.betterplan\.cl\/.*/,
      /^https:\/\/[^/]+\.fintoc\.com\/.*/,
    ],
    beforeSend: (event, _context) => {
      // Si no hay servicio de filtrado, enviar todo
      if (!errorFilterServiceInstance) {
        return true;
      }

      // Pasar el evento completo al servicio de filtrado
      // El servicio ahora es inteligente y puede manejar eventos de DataDog directamente
      if (errorFilterServiceInstance.shouldIgnoreError(event)) {
        return false; // No enviar este evento a DataDog
      }

      return true; // Enviar el evento
    },
  });
  datadogLogs.init({
    clientToken: 'pubb6528991197127a7ca3aa83f4b371d4a',
    site: 'us3.datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    env: environment.name.toLowerCase(),
    version: environment.version,
    service: 'bp-client-portal',
    forwardConsoleLogs: 'all',
  });
  // Sentry self-hosted (sentry.betterplan.cl, proyecto "portal"). Reemplaza a Sentry Cloud
  // (sentry.io): hasta el hotfix 9.3.3 cada evento se enviaba a los dos.
  Sentry.init({
    dsn: 'https://fa971dfb5ad47699e72bd11ee6ad8035@sentry.betterplan.cl/5',
    debug: !environment.production,
    release: environment.version,
    environment: environment.name,
    // Sentry Logs: el SDK los asocia al replay (pestaña Logs del replay). Solo warn/error de la
    // consola, para acotar el volumen.
    enableLogs: true,
    // Todo al 100 % (errores, traces y replays): el Sentry propio va a reemplazar a Datadog.
    // `sampleRate` (errores) por defecto es 1.0, así que no se setea explícitamente.
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    tracePropagationTargets: [/^https:\/\/[^/]+\.betterplan\.cl\/.*/],
    integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      replayIntegration({
        networkDetailAllowUrls: [
          window.location.origin,
          /^https:\/\/[^/]+\.betterplan\.cl\/.*/,
          /^https:\/\/[^/]+\.fintoc\.com\/.*/,
        ],
        networkRequestHeaders: ['X-Custom-Header'],
        networkResponseHeaders: ['X-Custom-Header'],
        blockAllMedia: false,
        maskAllText: false,
        maskAllInputs: false,
      }),
      replayCanvasIntegration(),
      // Acotado a dominios propios: el default de `failedRequestTargets` es
      // [/.*/] (TODAS las URLs), así que cualquier 5xx de terceros —Clarity,
      // Hotjar, Datadog— y del PROPIO ingest de Sentry se reportaba como error
      // del portal, con el mensaje sintético del SDK "HTTP Client Error with
      // status code: 5xx" y sin relación con lo que hacía el usuario.
      httpClientIntegration({
        failedRequestTargets: [/^https:\/\/[^/]+\.betterplan\.cl\/.*/],
      }),
      //  Sentry.browserProfilingIntegration(),
      //Sentry.metrics.metricsAggregatorIntegration(),
      //captureConsoleIntegration({
      //  levels: [
      /*'log', 'info', 'warn','debug','error', 'assert'*/
      //  ],
      // }),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      Sentry.browserTracingIntegration(),
    ],

    // `sendDefaultPii: false` quedó obsoleto en @sentry 10.x (se elimina en la 11) y lo reemplaza
    // `dataCollection`. Este bloque es el equivalente exacto que documenta Sentry: sin datos
    // personales automáticos (IP, cookies, headers y query params de identidad, cuerpos HTTP).
    // Un `dataCollection: {}` vacío sería MÁS permisivo, por eso cada categoría va explícita
    // (GraphQL, consultas a BD y variables locales vienen activadas por defecto; el portal no
    // usa esas integraciones, pero quedan apagadas para no abrir nada sin querer).
    dataCollection: {
      userInfo: false,
      genAI: { inputs: false, outputs: false },
      graphQL: { document: false, variables: false },
      databaseQueryData: false,
      stackFrameVariables: false,
      httpBodies: [],
      httpHeaders: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      cookies: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
    },
  });
  // LogRocket eliminado del portal (2026-08-26, confirmado por el equipo):
  // era uno de 5 grabadores de sesión simultáneos compitiendo en el arranque.
  // Sigue activo en bp-advisor/bp-admin; los identify del auth.service
  // compartido (bp-core) son no-ops acá sin el init.
  Hotjar.init(3637239, 6);
  // mixpanel.init('eb138694af8b89f7710564352bad1a48', {
  //   debug: false,
  //   track_pageview: true,
  //   persistence: 'localStorage',
  // });
  providers.push(
    // Usar el nuevo ProductionErrorHandler unificado que maneja Sentry, LogRocket y filtrado
    {
      provide: ErrorHandler,
      useClass: ProductionErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  );
} else {
  providers.push({ provide: ErrorHandler, useClass: DevelopErrorHandler });
}
registerLocaleData(localeESCL, localeESCLExtra);

bootstrapApplication(AppComponent, {
  providers: [
    // eventCoalescing agrupa los ticks de change detection disparados por un
    // mismo evento del DOM (menos pasadas de CD durante la carga del home).
    provideZoneChangeDetection({ eventCoalescing: true }),
    routerProviders,
    importProvidersFrom(
      BrowserModule,
      // Fuse, FuseConfig & FuseMockAPI
      // 3rd party modules that require global configuration via forRoot
      Angulartics2Module.forRoot(),
      OAuthModule.forRoot(),
      AngularFireModule.initializeApp(environment.firebase),
      AngularFirestoreModule,
      GoogleTagManagerModule.forRoot({
        id: 'GTM-N3S8WSB',
      }),
      MarkdownModule.forRoot({}),
      GrpcCoreModule.forRoot(),
      ImprobableEngGrpcWebClientModule.forChild({
        settings: {
          host: environment.urlGrpc,
          // we might want to use different transports as recommended by improbable-eng team
          // because websocket transport acts a bit differently and is intended for client streaming only
          transport: {
            unary: xhr,
            serverStream: xhr,
            clientStream: ws,
            bidiStream: ws,
          },
          // or simply e.g.
          // transport: ws, // to configure all methods to use websockets
        },
      }),
      GrpcLoggerModule.forRoot({
        settings: {
          // Logger SIEMPRE activo, en TODOS los ambientes incluida producción
          // (igual que bp-advisor y bp-admin): el log de request/response gRPC
          // en consola es la herramienta de diagnóstico del equipo en prod.
          // Revertido el 2026-09-02: el commit e44242ae2f lo había atado a
          // `localStorage.getItem('logger') === 'true' || !environment.production`
          // por el costo de serializar cada mensaje a JSON en el arranque, y eso
          // lo apagó en prod/qa/test/demo (los 4 tienen production: true).
          enabled: true,
          // protobuf json is more human-readable than the default toObject() mapping
          // please beware: if you use google.protobuf.Any you must pass the proper `messagePool` argument
          requestMapper: (msg: GrpcMessage) => msg.toProtobufJSON(),
          responseMapper: (msg: GrpcMessage) => msg.toProtobufJSON(),
        },
      }),
      // Firebase ya inicializado en líneas 359-360, no duplicar
      ServiceWorkerModule.register('ngsw-worker.js', {
        // 037 BT-6959: flag explícito por entorno (isDevMode() apagaba el worker en dev, que compila sin optimización)
        enabled: environment.enableServiceWorker,
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      }),
      MatSnackBarModule,

      // AppInsightsModule.forRoot({
      //   instrumentationKey: environment.appInsightsInstrumentationKey,
      // }),
    ),
    provideAnimations(),
    ...providers,
  ],
}).catch(err => {
  // Error crítico al inicializar la aplicación
  // Este error NO debe ser filtrado porque impide que la app arranque
  // Usar el método original del interceptor si está disponible
  const originalError = ConsoleErrorInterceptor.isActive()
    ? ConsoleErrorInterceptor.getOriginalError()
    : console.error;
  originalError('[Bootstrap Error] Failed to initialize application:', err);
});

/** La ruta más profunda de la rama principal: la de la página que se muestra. */
function deepestRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let current = route;
  while (current.firstChild) {
    current = current.firstChild;
  }
  return current;
}
